<script>
export default {
  name: "DidoDataset",
};
</script>

<script setup>
import { ref, watch, computed, provide } from "vue";
import api from "../api";
import {
  displayWithBR,
  displayDateFormatDDMMYYYY,
  cleanLinkedDocTitle,
  pause,
} from "../utils";
import DidoDatasetDownload from "./dataset/Download.vue";
import DidoResourcesPagination from "./commons/resources-pagination/Pagination.vue";
import DidoTextTruncate from "../components/commons/Truncate.vue";
import DidoChip from "./commons/chips/Chip.vue";
import DidoChipsList from "./commons/chips/List.vue";
import DidoChipsCollapsibleList from "./commons/chips/CollapsibleList.vue";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datasetId: { type: String, required: false, default: "" },
  dataset: { type: Object, required: false, default: null },
  selectedTab: { type: String, required: false, default: "datafiles" },
  linkedDocsApiUrl: { type: String, required: false, default: null },
});

// define events emitted
const emit = defineEmits([
  "dataset-retrieved",
  "dataset-not-retrieved",
  "label-selection",
  "datafile-selection",
  "bad-configuration",
  "tab-changed-in-dataset",
]);

// define Reactive Data
const status = ref("");
const loading = ref(false);
const datasetObject = ref(null);
const tags = ref([]);
const zones = ref([]);
const resourcesTab = ref(props.selectedTab);
const linkedDocs = ref([]);

// define watchers
watch(
  () => [props.apiUrl, props.datasetId, props.dataset],
  () => {
    status.value = "";
    loading.value = false;
    datasetObject.value = null;
    linkedDocs.value = [];
    tags.value = [];
    zones.value = [];
    getDataset();
  },
);
watch(
  () => [props.selectedTab],
  () => {
    resourcesTab.value = props.selectedTab;
  },
);

// create a computed flag to mark the widget as configured
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.datasetId && !props.dataset) return false;
  return true;
});

const onTabChanged = (newTab) =>
  emit("tab-changed-in-dataset", {
    tab: newTab,
    datasetId: datasetObject.value.id,
  });

// computed data for modification date
const modificationDate = computed(() => {
  const dateToDisplay =
    datasetObject.value.last_modified || datasetObject.value.created_at;
  return displayDateFormatDDMMYYYY(dateToDisplay);
});
// computed data for frequency
const frequency = computed(() => {
  if (datasetObject.value.frequency)
    return api.frequencies.find(
      ({ id }) => id === datasetObject.value.frequency,
    ).label;
  return "";
});
// computed data for frequency date
const frequencyDate = computed(() => {
  if (datasetObject.value.frequency_date)
    return displayDateFormatDDMMYYYY(datasetObject.value.frequency_date);
  return "";
});
// computed data for granularity
const granularity = computed(() => {
  if (datasetObject.value.spatial && datasetObject.value.spatial.granularity)
    return api.granularities.find(
      ({ id }) => id === datasetObject.value.spatial.granularity,
    ).label;
  return "";
});
// computed data for temporal coverage start and end dates
const temporalCoverageStart = computed(() => {
  if (datasetObject.value.temporal_coverage)
    return displayDateFormatDDMMYYYY(
      datasetObject.value.temporal_coverage.start,
    );
  return "";
});
const temporalCoverageEnd = computed(() => {
  if (datasetObject.value.temporal_coverage)
    return displayDateFormatDDMMYYYY(datasetObject.value.temporal_coverage.end);
  return "";
});
// computed data for documentations
const documentations = computed(() => {
  if (datasetObject.value.attachments)
    return [
      ...datasetObject.value.attachments.filter(
        ({ type }) => !type || type === "documentation",
      ),
    ];
  return [];
});
// computed data for historical datas
const historicalDatas = computed(() => {
  if (datasetObject.value.attachments)
    return datasetObject.value.attachments.filter(
      ({ type }) => type === "historical_data",
    );
  return [];
});

// Define methods
const getDataset = async () => {
  // if widget bad configured
  if (!configured.value) {
    status.value = "bad-configured";
    loading.value = false;
    datasetObject.value = null;
    emit("bad-configuration");
    return;
  }

  loading.value = true;

  // if dataset data is available , setting it in reactive datas (no api call required !!)
  if (props.dataset) {
    datasetObject.value = props.dataset;
    status.value = "available";
    loading.value = false;
  }
  // if dataset id is set, api call for retrieving dataset and setting it in reactive datas
  if (props.datasetId) {
    await Promise.all([
      api.getDataset(props.apiUrl, props.datasetId),
      pause(500),
    ])
      .then(([apiResult]) => {
        datasetObject.value = apiResult.dataset;
        status.value = apiResult.status;
        if (status.value !== "available")
          emit("dataset-not-retrieved", { datasetId: props.datasetId });
      })
      .finally(() => (loading.value = false));
    if (!datasetObject.value) return;

    emit("dataset-retrieved", { datasetId: datasetObject.value.id });
  }

  // get detail of zones
  zones.value = Promise.all(
    datasetObject.value.spatial.zones.map(async (id) =>
      api
        .getGeozone(props.apiUrl, id)
        .then((apiZone) => ({ id, display: apiZone.data.NAME }))
        .then((zone) => {
          let display = zone.display;
          if (zone.display === "France") display = "France entière";
          if (zone.display === "Metropolitan France")
            display = "France métropolitaine";
          return { id: zone.id, display };
        })
        .catch(() => id),
    ),
  );

  // get detail of tags
  tags.value = Promise.all(
    datasetObject.value.tags.map(async (id) =>
      api
        .getTag(props.apiUrl, id)
        .then((apiTag) => ({ id, display: apiTag.data.DISPLAY }))
        .catch(() => id),
    ),
  );

  // get linkedDocs
  if (props.linkedDocsApiUrl) {
    api
      .getDatasetLinkedDocs(props.linkedDocsApiUrl, datasetObject.value.id)
      .then((docs) => (linkedDocs.value = docs));
  }
};

// Init
getDataset();

// provide some infos of dataset for intercation with catalog
provide("getDatasetPageInfos", () =>
  datasetObject.value
    ? { id: datasetObject.value.id, title: datasetObject.value.title }
    : null,
);
</script>

<template>
  <div class="dido-widget-dataset relative-position">
    <!-- Loading is finished -->
    <template v-if="!loading">
      <!-- Bad configuration of widget -->
      <div
        v-if="status === 'bad-configured'"
        class="q-pa-sm row-start-center border-secondary text-black"
      >
        <div class="col-12">Impossible de charger le composant...</div>
        <div class="col-12">Les attributs suivant sont requis:</div>
        <ul class="col-12">
          <li>api-url</li>
          <li>dataset-id ou dataset</li>
        </ul>
      </div>
      <!-- Dataset unavailable-->
      <div
        v-if="!['available', 'bad-configured'].includes(status)"
        class="q-pa-sm column-all-center border-secondary text-black"
      >
        <div class="row-all-center">
          <QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-0-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          /><QIcon
            name="mdi-numeric-4-box-multiple-outline"
            size="md"
            class="q-mr-xs"
            color="secondary"
          />
        </div>
        <div class="row-all-center q-mt-md">
          Impossible d'afficher le jeu de données
          <QIcon
            name="mdi-sync"
            size="xs"
            class="on-right cursor-pointer"
            color="secondary"
            title="Réessayer de charger les données"
            @click="getDataset"
          />
        </div>
      </div>
      <!-- Dataset is here -->
      <div
        v-if="status === 'available'"
        class="dido-main-object row q-col-gutter-none"
      >
        <!-- Metadata -->
        <div class="bg-grey-4 col-12 row border-box q-pa-md">
          <!-- Title, Timestamps, Description and Tags -->
          <div
            class="full-height col-6 col-md-7 row content-start q-pr-md bordered-black-right"
          >
            <!-- Title -->
            <div class="col-12">
              <span class="dido-object-title">{{ datasetObject.title }}</span>
            </div>
            <!-- Timestamps -->
            <div
              :class="`col-12 q-my-sm row q-col-gutter-${
                $q.screen.gt.sm ? 'sm' : 'xs'
              } dido-object-timestamps`"
            >
              <!-- Modification Date -->
              <span class="col-auto"> Modifié le {{ modificationDate }} </span>
            </div>
            <!-- Description -->
            <div class="col-12">
              <DidoTextTruncate
                :text="datasetObject.description"
                :length="200"
                class="text-justify"
                :link-classes="[
                  'cursor-pointer',
                  'on-right',
                  'text-primary',
                  'text-underline',
                  'text-small',
                ]"
              />
            </div>
            <!-- Tags -->
            <div v-if="tags.length" class="col-12 q-mt-lg">
              <DidoChipsList
                type="tag"
                from-dataset-or-datafile="dataset"
                :items="tags"
                size="sm"
                bg-color="secondary"
                text-color="black"
                @label-selection="($event) => emit('label-selection', $event)"
              />
            </div>
          </div>
          <!-- Other Metadatas -->
          <div
            class="full-height col-6 col-md-5 row content-start q-pl-md q-col-gutter-x-none q-col-gutter-y-md"
          >
            <!-- Topic -->
            <div class="col-12 col-sm-6 col-md-4">
              <div
                :class="`text-left text-bold ${
                  $q.screen.gt.xs ? 'bordered-black-right' : ''
                }`"
              >
                THÈME
              </div>
              <div class="text-left">
                <DidoChip
                  type="topic"
                  from-dataset-or-datafile="dataset"
                  :item="{
                    id: datasetObject.topic,
                    display: datasetObject.topic,
                  }"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- License -->
            <div
              :class="`col-12 col-sm-6 col-md-4 ${
                $q.screen.gt.xs ? 'q-pl-sm' : ''
              }`"
            >
              <div
                :class="`text-left text-bold ${
                  $q.screen.gt.sm ? 'bordered-black-right' : ''
                }`"
              >
                LICENCE
              </div>
              <div class="text-left">
                <DidoChip
                  type="license"
                  from-dataset-or-datafile="dataset"
                  :item="{
                    id: datasetObject.license,
                    display: datasetObject.license,
                  }"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- Granularity -->
            <div
              v-if="granularity"
              :class="`col-12 col-sm-4 ${$q.screen.gt.sm ? 'q-pl-sm' : ''}`"
            >
              <div class="text-left text-bold">GRANULARITÉ</div>
              <div class="text-left">
                <DidoChip
                  type="granularity"
                  from-dataset-or-datafile="dataset"
                  :item="{
                    id: datasetObject.spatial.granularity,
                    display: granularity,
                  }"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- Frequency and FrequencyDate -->
            <div class="col-12">
              <div class="text-bold">FRÉQUENCE DE MISE À JOUR</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <span>
                  <DidoChip
                    type="frequency"
                    from-dataset-or-datafile="dataset"
                    :item="{ id: datasetObject.frequency, display: frequency }"
                    size="sm"
                    bg-color="secondary"
                    text-color="black"
                    @label-selection="
                      ($event) => emit('label-selection', $event)
                    "
                  />
                </span>
                <span v-if="frequencyDate" class="text-small">
                  Prochaine mise à jour le {{ frequencyDate }}
                </span>
              </div>
            </div>
            <!-- Geographical coverage -->
            <div v-if="zones.length" class="col-12">
              <div class="text-bold">COUVERTURE GÉOGRAPHIQUE</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <DidoChipsCollapsibleList
                  type="zone"
                  from-dataset-or-datafile="dataset"
                  :items="zones"
                  :items-when-collapsed="2"
                  size="sm"
                  bg-color="secondary"
                  text-color="black"
                  @label-selection="($event) => emit('label-selection', $event)"
                />
              </div>
            </div>
            <!-- Temporal coverage -->
            <div v-if="datasetObject.temporal_coverage" class="col-12">
              <div class="text-left text-bold">COUVERTURE TEMPORELLE</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <span class="text-small">
                  {{ temporalCoverageStart }} au {{ temporalCoverageEnd }}
                </span>
              </div>
            </div>
            <!-- Caution -->
            <div v-if="datasetObject.caution" class="col-12">
              <div class="text-left text-bold">PRÉCAUTIONS D'USAGE</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <div
                  class="text-justify text-small"
                  v-html="displayWithBR(datasetObject.caution)"
                />
              </div>
            </div>
            <!-- Consultations Counter -->
            <div v-if="datasetObject.views" class="col-12">
              <div class="text-left text-bold">NOMBRE DE CONSULTATIONS</div>
              <div class="row-start-center no-wrap q-col-gutter-x-sm">
                <span class="text-small">
                  {{ datasetObject.views.toLocaleString() }}
                </span>
              </div>
            </div>
            <!-- Linked Docs -->
            <div v-if="linkedDocs.length" class="col-12">
              <div class="text-left text-bold">PUBLICATIONS ASSOCIÉES</div>
              <div
                v-for="(linkedDoc, i) of linkedDocs"
                :key="`linkedDoc-${i}`"
                class="text-left"
              >
                <QBtn
                  :href="linkedDoc.url"
                  target="_blank"
                  icon-right="mdi-open-in-new"
                  size="sm"
                  flat
                  dense
                  class="text-small"
                  no-caps
                  no-wrap
                  color="secondary"
                  :title="`Ouvrir la publication '${cleanLinkedDocTitle(
                    linkedDoc.title,
                  )}' dans une nouvelle fenêtre`"
                  :label="cleanLinkedDocTitle(linkedDoc.title)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Resources -->
        <div class="q-mt-xl col-12">
          <div class="q-px-md relative-position">
            <QTabs
              v-model="resourcesTab"
              no-caps
              align="left"
              narrow-indicator
              dense
              active-bg-color="white"
              active-color="secondary"
              indicator-color="secondary"
              @update:model-value="onTabChanged"
            >
              <QTab name="datafiles" label="Fichiers de données" />
              <QTab name="documentations" label="Documentations" />
              <QTab
                v-if="historicalDatas.length"
                name="hist_datas"
                label="Données historiques"
              />
              <QTab name="download" label="Téléchargement" />
            </QTabs>
            <QTabPanels v-model="resourcesTab" animated>
              <QTabPanel name="datafiles" class="q-px-none">
                <DidoResourcesPagination
                  :api-url="props.apiUrl"
                  :resources="datasetObject.datafiles"
                  type="datafiles"
                  @datafile-selection="
                    ($event) =>
                      emit('datafile-selection', {
                        datasetId: datasetObject.id,
                        datafileRid: $event.datafileRid,
                      })
                  "
                />
              </QTabPanel>
              <QTabPanel name="documentations" class="q-px-none">
                <DidoResourcesPagination
                  :api-url="props.apiUrl"
                  :resources="documentations"
                  type="documentations"
                />
              </QTabPanel>
              <QTabPanel
                v-if="historicalDatas.length"
                name="hist_datas"
                class="q-px-none"
              >
                <DidoResourcesPagination
                  :api-url="props.apiUrl"
                  :resources="historicalDatas"
                  type="historicalDatas"
                />
              </QTabPanel>
              <QTabPanel name="download" class="q-px-none">
                <DidoDatasetDownload
                  :api-url="props.apiUrl"
                  :dataset="datasetObject"
                />
              </QTabPanel>
            </QTabPanels>
          </div>
        </div>
      </div>
    </template>

    <!-- Spinner during interaction with api -->
    <QInnerLoading :showing="loading">
      <QSpinner size="50px" color="secondary" />
      <div class="text-h6 text-secondary">
        Chargement du jeu de données en cours
      </div>
    </QInnerLoading>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-dataset
  min-height: 200px
  .dido-object-title
    word-break: break-word
  .dido-dataset-linked-docs-title
    font-size: 1.3em
    font-weight: 600
</style>
